<template>
  <AccountShell allow-guest>
    <article class="favorites" aria-labelledby="favorites-title">
      <header class="favorites__header">
        <div>
          <p class="eyebrow">{{ copy.eyebrow }}</p>
          <h1 id="favorites-title" tabindex="-1">{{ copy.title }}</h1>
          <p>{{ copy.intro }}</p>
        </div>
        <span v-if="!loading" class="count" aria-live="polite">{{
          countLabel
        }}</span>
      </header>

      <p class="live" role="status" aria-live="polite">{{ status }}</p>
      <section
        v-if="cartOutcome"
        class="outcome"
        :class="{ partial: cartOutcome.failed.length }"
        role="status"
        aria-labelledby="cart-outcome-title"
      >
        <h2 id="cart-outcome-title" tabindex="-1">{{ cartOutcomeHeading }}</h2>
        <p>{{ cartOutcomeMessage }}</p>
        <ul v-if="cartOutcome.failed.length">
          <li v-for="failure in cartOutcome.failed" :key="failure.id">
            <strong>{{ failure.name }}</strong
            >: {{ failure.message }}
          </li>
        </ul>
        <button type="button" @click="cartOutcome = null">
          {{ copy.dismiss }}
        </button>
      </section>

      <div
        v-if="loading"
        class="skeletons"
        role="status"
        aria-label="Loading favorites"
      >
        <div
          v-for="index in 3"
          :key="index"
          class="skeleton"
          aria-hidden="true"
        ></div>
        <span class="sr-only">{{ copy.loading }}</span>
      </div>
      <section v-else-if="error && !items.length" class="error" role="alert">
        <h2>{{ copy.loadErrorTitle }}</h2>
        <p>{{ error }}</p>
        <button type="button" @click="retryFavorites">
          {{ copy.tryAgain }}
        </button>
      </section>
      <section v-else-if="!items.length" class="empty">
        <div aria-hidden="true" class="empty__heart">♡</div>
        <h2>{{ copy.emptyTitle }}</h2>
        <p>{{ copy.emptyBody }}</p>
        <div class="actions">
          <router-link to="/eyeglasses">{{ copy.shopEyewear }}</router-link
          ><router-link
            class="secondary"
            to="/eyeglasses?sort=date_added&order=DESC"
            >{{ copy.newArrivals }}</router-link
          >
        </div>
      </section>
      <template v-else>
        <section
          v-if="error"
          class="recoverable-warning"
          role="alert"
          aria-labelledby="favorites-warning-title"
        >
          <h2 id="favorites-warning-title">{{ copy.recoverableTitle }}</h2>
          <p>{{ error }}</p>
          <button type="button" @click="retryFavorites">
            {{ copy.retryFavorites }}
          </button>
        </section>
        <div class="toolbar">
          <button
            type="button"
            :disabled="addingAll || cartPending.length > 0"
            @click="addAll"
          >
            {{ addingAll ? copy.addingAll : copy.addAll }}
          </button>
          <button
            type="button"
            class="danger-link"
            :disabled="addingAll || clearing"
            @click="openClear"
          >
            {{ copy.clear }}
          </button>
        </div>
        <NewProductGrid
          :products="catalogItems"
          :wishlist="items"
          is-grid
          :mobile-columns="2"
          :max-gallery-images="3"
          list-name="Favorites"
          item-role="listitem"
          role="list"
          aria-label="Saved products"
        >
          <template #footer-actions="{ product }">
            <div class="card__actions">
              <button
                v-if="product.cartEligible"
                type="button"
                :disabled="
                  addingAll ||
                  pending(product.id) ||
                  cartPending.includes(product.id)
                "
                @click="addOne(product)"
              >
                {{
                  cartPending.includes(product.id) ? copy.adding : copy.addToBag
                }}
              </button>
              <router-link
                v-else-if="
                  product.available && product.href && product.requiresOptions
                "
                :to="product.href"
                >{{ copy.chooseOptions }}</router-link
              >
              <button
                type="button"
                class="secondary"
                :disabled="addingAll || pending(product.id)"
                @click="remove(product)"
              >
                {{ pending(product.id) ? copy.removing : copy.remove }}
              </button>
            </div>
          </template>
        </NewProductGrid>
      </template>

      <div v-if="confirmClear" class="backdrop" @click.self="closeClear">
        <section
          ref="dialog"
          class="dialog"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="clear-title"
          tabindex="-1"
          @keydown.esc="closeClear"
          @keydown.tab="trapFocus"
        >
          <h2 id="clear-title">{{ copy.clearTitle }}</h2>
          <p>{{ copy.clearBody }}</p>
          <div class="actions">
            <button ref="cancelButton" type="button" @click="closeClear">
              {{ copy.keep }}</button
            ><button
              type="button"
              class="danger"
              :disabled="clearing || addingAll"
              @click="clearAll"
            >
              {{ clearing ? copy.clearing : copy.clearConfirm }}
            </button>
          </div>
        </section>
      </div>
    </article>
  </AccountShell>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useMeta } from "vue-meta";
import { useStore } from "vuex";
import AccountShell from "../../../account/AccountShell.vue";
import NewProductGrid from "../../../organisms/new-product-grid/new-product-grid.vue";
import { createWishlistCartCoordinator } from "~/core/account/wishlistCartCoordinator";
import { retryWishlistRecovery } from "~/core/account/wishlistRecovery";

type Failure = { id: string; name: string; message: string };
const store = useStore();
const { tm } = useI18n();
const copy = tm("pages.store.wishlist") as Record<string, string>;
const items = computed(() => store.getters["store/wishlist/get"] || []);
const catalogItems = computed(() => items.value.map(catalogProduct));
const count = computed(() => store.getters["store/wishlist/count"] || 0);
const loading = computed(() => store.getters["store/wishlist/loading"]);
const error = computed(() => store.getters["store/wishlist/error"]);
const authenticated = computed(() => store.getters["common/customer/auth"]);
const pending = (id: string) => store.getters["store/wishlist/pending"](id);
const countLabel = computed(
  () => `${count.value} ${count.value === 1 ? copy.item : copy.items}`
);
const status = ref("");
const addingAll = ref(false);
const cartPending = ref<string[]>([]);
const cartOutcome = ref<{ added: number; failed: Failure[] } | null>(null);
const confirmClear = ref(false);
const clearing = ref(false);
const dialog = ref<HTMLElement | null>(null);
const cancelButton = ref<HTMLButtonElement | null>(null);
const previousFocus = ref<HTMLElement | null>(null);
const cartCoordinator = createWishlistCartCoordinator((snapshot) => {
  addingAll.value = snapshot.addingAll;
  cartPending.value = snapshot.pending;
});
function catalogProduct(item: any) {
  const previousPrice = Number(item.previousPriceRaw);
  const currentPrice = Number(item.priceRaw);
  const discounted =
    item.priceChanged &&
    Number.isFinite(previousPrice) &&
    Number.isFinite(currentPrice) &&
    currentPrice < previousPrice;

  return {
    ...item,
    id: String(item.id),
    url: item.url || item.href || "",
    image: item.image || item.imageLazy || "",
    imageLazy: item.imageLazy || item.image || "",
    imageBig: item.image || item.imageLazy || "",
    images: Array.isArray(item.images) ? item.images : [],
    manufacturer: item.manufacturer || { name: item.model || "" },
    price: discounted ? item.previousPriceFormatted : item.price,
    special: discounted ? item.price : "",
    stock: item.inStock,
    low_stock:
      item.lowStock || /low stock/i.test(String(item.stockStatus || "")),
    categories: item.finalSale ? [{ id: "62" }] : item.categories || [],
  };
}
const cartOutcomeHeading = computed(() =>
  cartOutcome.value?.failed.length ? copy.partialTitle : copy.addedTitle
);
const cartOutcomeMessage = computed(() => {
  if (!cartOutcome.value) return "";
  return `${cartOutcome.value.added} ${
    cartOutcome.value.added === 1 ? copy.addedOne : copy.addedMany
  }${
    cartOutcome.value.failed.length
      ? ` ${cartOutcome.value.failed.length} ${
          cartOutcome.value.failed.length === 1
            ? copy.failedOne
            : copy.failedMany
        }`
      : ""
  }`;
});

async function load() {
  cartOutcome.value = null;
  await store.dispatch("store/wishlist/load");
}
async function retryFavorites() {
  cartOutcome.value = null;
  await retryWishlistRecovery(store.dispatch, authenticated.value);
}
async function remove(item: any) {
  const result = await store.dispatch("store/wishlist/removeExplicit", {
    productId: item.id,
  });
  status.value = result.ok
    ? `${item.name} ${copy.removedStatus}`
    : copy.removeFailed;
}
async function addOne(item: any) {
  const execution = await cartCoordinator.runOne(item, (product) =>
    store.dispatch("store/cart/add", {
      product,
      quantity: product.minimum || 1,
      options: [],
    })
  );
  if (execution.skipped) return;
  const result = execution.result;
  cartOutcome.value = result?.ok
    ? { added: 1, failed: [] }
    : {
        added: 0,
        failed: [
          {
            id: item.id,
            name: item.name,
            message: result?.error?.message || copy.cartFailed,
          },
        ],
      };
}
async function addAll() {
  if (addingAll.value || cartPending.value.length > 0) return;
  const failed: Failure[] = [];
  let added = 0;
  const eligible: any[] = [];
  for (const item of items.value) {
    if (!item.cartEligible) {
      failed.push({
        id: item.id,
        name: item.name,
        message: item.requiresOptions
          ? copy.optionsRequired
          : copy.unavailableCart,
      });
      continue;
    }
    eligible.push(item);
  }
  const executions = await cartCoordinator.runAll(eligible, (product) =>
    store.dispatch("store/cart/add", {
      product,
      quantity: product.minimum || 1,
      options: [],
    })
  );
  for (const execution of executions) {
    const item = execution.item;
    const result = execution.result;
    if (result?.ok) added += 1;
    else
      failed.push({
        id: item.id,
        name: item.name,
        message: result?.error?.message || copy.cartFailed,
      });
  }
  cartOutcome.value = { added, failed };
  await nextTick();
  document.getElementById("cart-outcome-title")?.focus();
}
function openClear(event: Event) {
  if (addingAll.value || clearing.value) return;
  previousFocus.value = event.currentTarget as HTMLElement;
  confirmClear.value = true;
  nextTick(() => cancelButton.value?.focus());
}
function closeClear() {
  confirmClear.value = false;
  nextTick(() => previousFocus.value?.focus());
}
function trapFocus(event: KeyboardEvent) {
  const controls = Array.from(
    dialog.value?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    ) || []
  );
  if (!controls.length) return;
  const first = controls[0],
    last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
async function clearAll() {
  if (addingAll.value || clearing.value) return;
  clearing.value = true;
  let result: any;
  try {
    result = await store.dispatch("store/wishlist/clear");
  } catch {
    result = { ok: false, error: { message: copy.clearFailed } };
  } finally {
    clearing.value = false;
  }
  if (result.ok) {
    confirmClear.value = false;
    cartOutcome.value = null;
    status.value = copy.clearedStatus;
    await nextTick();
    document.getElementById("favorites-title")?.focus();
  } else {
    confirmClear.value = false;
    status.value = result.error?.message || copy.clearFailed;
  }
}
onMounted(load);
useMeta({
  title: copy.metaTitle,
  meta: [{ name: "robots", content: "noindex, nofollow, noarchive" }],
});
</script>

<style scoped>
.favorites {
  min-width: 0;
  color: #071c39;
}
.favorites__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}
.eyebrow {
  margin: 0;
  color: #59616b;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.favorites h1 {
  margin: 0.2rem 0;
  font-size: clamp(2rem, 5vw, 3rem);
}
.count {
  padding: 0.5rem 0.75rem;
  border: 1px solid #071c39;
  border-radius: 999px;
  font-weight: 750;
}
.live {
  min-height: 1.5rem;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0;
}
.toolbar button,
.card__actions button,
.card__actions a,
.outcome button,
.recoverable-warning button,
.error button,
.actions a,
.actions button {
  min-height: 44px;
  box-sizing: border-box;
  padding: 0.65rem 0.9rem;
  border: 2px solid #071c39;
  background: #071c39;
  color: #fff;
  font: inherit;
  font-weight: 750;
  text-decoration: none;
}
.danger-link {
  border-color: transparent !important;
  background: transparent !important;
  color: #9d1c13 !important;
}
.favorites :deep(.vf-o-new-product-grid__container.grid-view) {
  display: grid;
  /* The guest account shell is 960px wide; three catalog-width cards preserve
     the catalogue proportions instead of compressing the media area. */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  justify-content: stretch;
}
.favorites :deep(.vf-o-new-product-grid__item) {
  min-width: 0;
  width: 100%;
  max-width: none;
  flex: none;
  justify-self: stretch;
  box-sizing: border-box;
  background: #fff;
}
.card__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem;
  margin-top: auto;
  padding: 0 0.9rem 0.9rem;
}
.card__actions .secondary {
  background: #fff;
  color: #071c39;
}
.empty,
.error,
.outcome,
.recoverable-warning {
  padding: 1.5rem;
  border: 1px solid #d8d6cf;
  border-radius: 0.45rem;
  background: #fff;
}
.empty {
  text-align: center;
}
.empty__heart {
  font-size: 4rem;
  color: #ee7203;
}
.actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.actions .secondary {
  background: #fff;
  color: #071c39;
}
.outcome.partial {
  border-left: 5px solid #ee7203;
}
.recoverable-warning {
  margin: 1rem 0;
  border-left: 5px solid #ee7203;
  background: #fff7ed;
}
.outcome ul {
  padding-left: 1.3rem;
}
.skeletons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}
.skeleton {
  height: 22rem;
  background: linear-gradient(100deg, #e8e6df 30%, #f7f6f2 50%, #e8e6df 70%);
  background-size: 300% 100%;
  animation: pulse 1.5s infinite;
}
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(7, 28, 57, 0.7);
}
.dialog {
  max-width: 440px;
  padding: 1.5rem;
  background: #fff;
}
.dialog .danger {
  background: #9d1c13;
  border-color: #9d1c13;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
button:focus-visible,
a:focus-visible,
.dialog:focus {
  outline: 3px solid #ee7203;
  outline-offset: 2px;
}
@keyframes pulse {
  to {
    background-position: -200% 0;
  }
}
@media (max-width: 640px) {
  .favorites__header {
    display: grid;
  }
  .skeletons {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .toolbar {
    display: grid;
  }
  .card__actions {
    grid-template-columns: 1fr;
  }
  .favorites :deep(.vf-o-new-product-grid__container.grid-view) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }
}
@media (max-width: 320px) {
  .card__actions {
    padding: 0.75rem;
  }
  .favorites__header {
    overflow-wrap: anywhere;
  }
}
@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
  }
}
</style>
